// src/components/GenerateLoadingOverlay.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export function GenerateLoadingOverlay({
  open,
  title = "Generating your project",
  subtitle = "Analyzing inputs and generating structured insights…",
  variant = "project",
  steps = [
    "Reading research data",
    "Extracting evidence-backed tags",
    "Clustering themes",
    "Generating insights",
    "Final consistency checks",
  ],
  progress,
  error,
  onRetry,
  onNewProject,
  stepAdvanceMs = 3600,
  fakeProgressTimeConstant = 2.2,
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const [fakeProgress, setFakeProgress] = useState(0);

  const hasRealProgress = typeof progress === "number" && !Number.isNaN(progress);
  const pct = Math.round(clamp((hasRealProgress ? progress : fakeProgress) * 100, 0, 99));

  useEffect(() => {
    if (!open) return;
    setStepIndex(0);
    if (hasRealProgress) return;
    const interval = setInterval(() => {
      setStepIndex((i) => {
        const last = Math.max(steps.length - 1, 0);
        return i >= last ? last : i + 1;
      });
    }, stepAdvanceMs);
    return () => clearInterval(interval);
  }, [open, steps.length, hasRealProgress, stepAdvanceMs]);

  useEffect(() => {
    if (!open) {
      setFakeProgress(0);
      return;
    }
    if (hasRealProgress) return;

    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = (now - start) / 1000;
      const next = 1 - Math.exp(-t / fakeProgressTimeConstant);
      setFakeProgress(clamp(next * 0.98, 0, 0.98));
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, hasRealProgress, fakeProgressTimeConstant]);

  const currentStep = useMemo(() => steps[stepIndex] || null, [steps, stepIndex]);
  const errorView = useMemo(() => {
    if (!error) return null;
    if (typeof error === "string") {
      return {
        title: "Could not process this file",
        body: error,
        details: [],
        retryable: true,
      };
    }
    return {
      title: error.title || "Could not process this file",
      body: error.body || "Something went wrong while processing this file. Please try again.",
      details: Array.isArray(error.details) ? error.details : [],
      retryable: error.retryable !== false,
    };
  }, [error]);

  useEffect(() => {
    if (!open || !hasRealProgress) return;
    const idx = Math.min(
      Math.max(Math.floor((steps.length || 1) * (progress || 0)), 0),
      Math.max(steps.length - 1, 0)
    );
    setStepIndex(idx);
  }, [hasRealProgress, open, progress, steps.length]);

  if (!open) return null;

  const icons =
    variant === "overview"
      ? [
          {
            key: "collect",
            label: "Collecting approved insights",
            svg: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M4 7h10M4 12h7M4 17h8" strokeLinecap="round" />
                <path d="M15 7h5M15 12h5M15 17h5" strokeLinecap="round" />
              </svg>
            ),
          },
          {
            key: "synthesize",
            label: "Synthesizing patterns",
            svg: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="6" cy="12" r="2.5" />
                <circle cx="18" cy="7" r="2.5" />
                <circle cx="18" cy="17" r="2.5" />
                <path d="M8.2 11l6.6-3.2M8.2 13l6.6 3.2" strokeLinecap="round" />
              </svg>
            ),
          },
          {
            key: "draft",
            label: "Drafting overview sections",
            svg: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M6 4h9l3 3v13a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2z" />
                <path d="M9 12h6M9 16h6" strokeLinecap="round" />
              </svg>
            ),
          },
          {
            key: "finalize",
            label: "Finalizing evidence links",
            svg: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M8 12h8M12 8v8" strokeLinecap="round" />
                <circle cx="12" cy="12" r="7" />
              </svg>
            ),
          },
        ]
      : variant === "story"
      ? [
          {
            key: "arc",
            label: "Structuring story arc",
            svg: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M4 17c3-6 6-10 10-10 3 0 5 2 6 5" strokeLinecap="round" />
                <circle cx="5" cy="17" r="1.5" />
                <circle cx="14" cy="7" r="1.5" />
                <circle cx="20" cy="12" r="1.5" />
              </svg>
            ),
          },
          {
            key: "frames",
            label: "Building story frames",
            svg: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="4" y="5" width="6" height="6" rx="1.2" />
                <rect x="14" y="5" width="6" height="6" rx="1.2" />
                <rect x="4" y="13" width="6" height="6" rx="1.2" />
                <rect x="14" y="13" width="6" height="6" rx="1.2" />
              </svg>
            ),
          },
          {
            key: "prompts",
            label: "Writing image prompts",
            svg: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M7 4h8l4 4v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z" />
                <path d="M9 12h6M9 16h5" strokeLinecap="round" />
              </svg>
            ),
          },
          {
            key: "visuals",
            label: "Starting visual generation",
            svg: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="4" y="6" width="16" height="12" rx="2" />
                <path d="M8 14l2.8-3 3 3.5 2-2 2.2 2.5" strokeLinecap="round" />
                <circle cx="9" cy="10" r="1.2" />
              </svg>
            ),
          },
        ]
      : [
          {
            key: "doc",
            label: "Reading research data",
            svg: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M7 8h10M7 12h6M7 16h8" strokeLinecap="round" />
                <path
                  d="M6 3h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2z"
                  strokeLinecap="round"
                />
              </svg>
            ),
          },
          {
            key: "tags",
            label: "Extracting evidence-backed tags",
            svg: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M20 12l-8 8-8-8 8-8h5l3 3v5z" strokeLinecap="round" />
                <circle cx="14" cy="10" r="1.7" fill="currentColor" />
              </svg>
            ),
          },
          {
            key: "themes",
            label: "Clustering themes",
            svg: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="6" cy="12" r="2.5" />
                <circle cx="18" cy="7" r="2.5" />
                <circle cx="18" cy="17" r="2.5" />
                <path d="M8.2 11l6.6-3.2M8.2 13l6.6 3.2" strokeLinecap="round" />
              </svg>
            ),
          },
          {
            key: "insights",
            label: "Generating insights",
            svg: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M4 19h16" strokeLinecap="round" />
                <rect x="6" y="10" width="3" height="7" rx="1" />
                <rect x="11" y="6" width="3" height="11" rx="1" />
                <rect x="16" y="12" width="3" height="5" rx="1" />
              </svg>
            ),
          },
          {
            key: "final",
            label: "Final consistency checks",
            svg: (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M12 3l2.1 4.3 4.7.7-3.4 3.3.8 4.7L12 13.7 7.8 16l.8-4.7L5.2 8l4.7-.7L12 3z" />
              </svg>
            ),
          },
        ];

  const content = (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center px-6" aria-busy="true">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-indigo-100" />

      <div className="relative z-10 flex flex-col items-center text-center w-full max-w-5xl">
        <h2 className="text-3xl md:text-4xl font-semibold text-slate-900">{title}</h2>
        <p className="mt-3 text-base md:text-lg text-slate-600 max-w-2xl">{subtitle}</p>

        {!errorView ? (
          <div className="mt-10 flex flex-wrap items-center justify-center gap-6">
            {icons.map((icon, idx) => {
              const isActive = idx === stepIndex;
              const isDone = idx < stepIndex;
              const isFuture = idx > stepIndex;

              const tileClasses = [
                "relative flex h-20 w-20 md:h-24 md:w-24 items-center justify-center rounded-3xl",
                "transition-all duration-300",
                isActive
                  ? "bg-indigo-100 text-indigo-700 scale-[1.08] ring-2 ring-indigo-200/80"
                  : isDone
                    ? "bg-indigo-50 text-indigo-500"
                    : "bg-white/70 text-slate-400 border border-slate-200",
                isFuture ? "opacity-70" : "opacity-100",
                isActive ? "glow-breath" : "",
              ].join(" ");

              return (
                <div key={icon.key} className="flex flex-col items-center gap-3">
                  <div className={tileClasses}>
                    <div className="h-10 w-10 md:h-12 md:w-12">{icon.svg}</div>
                    {isActive ? <span className="active-underline" /> : null}
                  </div>
                  <div className="text-xs text-slate-500 max-w-[120px] leading-tight mt-[5px]">
                    {icon.label}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-10 w-full max-w-xl rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-left">
            <div className="text-sm font-semibold text-rose-700">{errorView.title}</div>
            <div className="mt-1 text-sm text-rose-700/90">{errorView.body}</div>
            {errorView.details.length ? (
              <div className="mt-3 space-y-1 text-sm text-rose-700/90">
                {errorView.details.map((detail) => (
                  <div key={detail}>{detail}</div>
                ))}
              </div>
            ) : null}
            {(typeof onRetry === "function" && errorView.retryable) || typeof onNewProject === "function" ? (
              <div className="mt-4">
                <div className="flex flex-wrap items-center gap-3">
                  {typeof onRetry === "function" && errorView.retryable ? (
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                      onClick={onRetry}
                    >
                      Try again
                    </button>
                  ) : null}
                  {typeof onNewProject === "function" ? (
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      onClick={onNewProject}
                    >
                      New Project
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        )}

        {currentStep && !errorView ? (
          <p className="mt-7 text-sm md:text-base text-indigo-600 font-medium">
            {currentStep}
          </p>
        ) : null}

        {!errorView ? (
          <div className="mt-10 w-72 md:w-96">
            <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-500 via-indigo-400 to-indigo-600 animate-progress-sheen"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Generation may take a little longer in this MVP version.
            </p>
          </div>
        ) : null}
      </div>

      <style>{`
        .glow-breath {
          box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.2);
          animation: glow-breath 1.8s ease-in-out infinite;
        }
        @keyframes glow-breath {
          0% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.18); }
          50% { box-shadow: 0 0 0 10px rgba(99, 102, 241, 0.12); }
          100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.16); }
        }
        .active-underline {
          position: absolute;
          bottom: -10px;
          left: 20%;
          width: 60%;
          height: 3px;
          border-radius: 999px;
          background: linear-gradient(90deg, #6366f1, #a5b4fc);
          animation: underline-slide 1.6s ease-in-out infinite;
        }
        @keyframes underline-slide {
          0% { transform: translateX(-6px); opacity: 0.6; }
          50% { transform: translateX(6px); opacity: 1; }
          100% { transform: translateX(-6px); opacity: 0.6; }
        }
        .animate-progress-sheen {
          background-size: 200% 100%;
          animation: progress-sheen 1.4s linear infinite;
        }
        @keyframes progress-sheen {
          0% { background-position: 0% 0%; }
          100% { background-position: 100% 0%; }
        }
      `}</style>
    </div>
  );

  return createPortal(content, document.body);
}
